import { useContext } from "react";
import {
  Group,
  Text,
  Avatar,
  Menu,
  useMantineColorScheme,
  ActionIcon,
  Button,
  Divider,
} from "@mantine/core";
import {
  IconSun,
  IconMoonStars,
  IconLogout,
  IconLayoutSidebar,
  IconLogin2,
  IconUserPlus,
  IconUpload,
  IconUserCog,
  IconChevronRight,
} from "@tabler/icons-react";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Logo from "./Logo";
import OrgContextBadge from "./OrgContextBadge";
import TargetOrgBadge from "./TargetOrgBadge";
import { UploadContext } from "../contexts/UploadContext";
import UploadsMenu from "./UploadsMenu";
import ExportsMenu from "./ExportsMenu";
import { useMediaQuery } from "@mantine/hooks";
import { OrgTypes, UserLevels } from "../utils/constants";

function Header({ toggleSidebar, toggleDrawer }) {
  const isSmallScreen = useMediaQuery("(max-width: 768px)");
  const { user, isAuthenticated, logout, orgOptions, selectedOrgId } =
    useContext(AuthContext);
  const { setUploadsModalOpen } = useContext(UploadContext);
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const dark = colorScheme === "dark";

  const activeOrg = orgOptions.find(
    (o) => String(o.id) === String(selectedOrgId),
  );

  // "Working in" switch: staff can switch into any org (the isStaff flag grants
  // member-equivalent rights everywhere, no membership required), other users
  // only when they belong to more than one org. A single-membership user has
  // nothing to switch, so the switch is hidden.
  const canSwitchWorkingIn = user?.isStaff || (user?.membershipCount ?? 0) > 1;
  const showOrgBadge =
    isAuthenticated &&
    !isSmallScreen &&
    canSwitchWorkingIn &&
    orgOptions.length > 0;

  // "Target" switch: shown whenever the working-in org is a Contractor, so the
  // user can pick which linked asset owner to operate against. Independent of
  // the working-in switch - e.g. a single-membership contractor sees Target but
  // not the working-in switcher.
  const showTargetBadge =
    isAuthenticated &&
    !isSmallScreen &&
    String(activeOrg?.orgType) === String(OrgTypes.CONTRACTOR);

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    }
    return "";
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <Group
      h="100%"
      px="md"
      wrap="nowrap"
      justify="space-between"
      align="center"
    >
      <Group gap="sm" align="center" wrap="nowrap">
        {isAuthenticated && (
          <>
            <ActionIcon
              onClick={toggleSidebar}
              color="gray"
              c="var(--mantine-color-text)"
              size="md"
              variant="subtle"
              visibleFrom="md"
              data-testid="toggle-sidebar-button"
            >
              <IconLayoutSidebar size={18} stroke={1.6} />
            </ActionIcon>
            <ActionIcon
              onClick={toggleDrawer}
              color="gray"
              c="var(--mantine-color-text)"
              size="md"
              variant="subtle"
              hiddenFrom="md"
              data-testid="toggle-drawer-button"
            >
              <IconLayoutSidebar size={18} stroke={1.6} />
            </ActionIcon>
          </>
        )}
        <Logo
          showTitle={!isSmallScreen}
          subLabel={user?.isStaff ? "Admin" : undefined}
        />
      </Group>

      <Group gap="sm" align="center" wrap="nowrap">
        {isAuthenticated && (
          <>
            <Button
              variant="filled"
              color="teal"
              size="xs"
              leftSection={<IconUpload size={16} />}
              onClick={() => setUploadsModalOpen(true)}
              data-testid="upload-button"
            >
              Upload
            </Button>
            <UploadsMenu />
            {user?.userLevel !== UserLevels.UPLOAD_ONLY && <ExportsMenu />}
          </>
        )}
        {isAuthenticated && (showOrgBadge || showTargetBadge) && (
          <Divider
            orientation="vertical"
            h={18}
            style={{ alignSelf: "center" }}
          />
        )}
        {showOrgBadge && <OrgContextBadge />}
        {showOrgBadge && showTargetBadge && (
          <IconChevronRight
            size={14}
            stroke={3}
            style={{ color: "var(--mantine-color-dimmed)" }}
          />
        )}
        {showTargetBadge && activeOrg && (
          <TargetOrgBadge contractor={activeOrg} />
        )}
        <Menu shadow="md" width={250}>
          <Menu.Target>
            <Avatar
              style={{ cursor: "pointer" }}
              size={32}
              radius="xl"
              color="teal"
              variant="light"
              data-testid="user-menu-button"
            >
              {getUserInitials()}
            </Avatar>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Account</Menu.Label>
            {isAuthenticated ? (
              <Menu.Item
                leftSection={<Avatar size={24}>{getUserInitials()}</Avatar>}
                data-testid="user-profile-menu-item"
              >
                <Text size="sm" fw="normal">
                  {user?.firstName} {user?.lastName}
                </Text>
                <Text size="xs" c="dimmed">
                  {user?.email}
                </Text>
              </Menu.Item>
            ) : (
                           <>
                <Menu.Item
                  leftSection={<IconLogin2 size={18} />}
                  onClick={() => navigate("/login")}
                  data-testid="login-menu-item"
                >
                  Login
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconUserPlus size={18} />}
                  onClick={() => navigate("/register")}
                  data-testid="signup-menu-item"
                >
                  Sign Up
                </Menu.Item>
              </>
            )}
            <Menu.Divider />
            <Menu.Item
              leftSection={
                dark ? <IconSun size={18} /> : <IconMoonStars size={18} />
              }
              onClick={toggleColorScheme}
              data-testid="theme-toggle-menu-item"
            >
              {dark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            </Menu.Item>
            {isAuthenticated && (
              <>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconUserCog size={18} />}
                  onClick={() => navigate("/account")}
                  data-testid="account-settings-menu-item"
                >
                  Account Settings
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconLogout size={18} />}
                  onClick={handleLogout}
                  data-testid="logout-menu-item"
                >
                  Logout
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}

export default Header;
